import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Scale,
  Briefcase,
  Gavel,
  Calculator,
  Landmark,
  Compass,
  AlertCircle,
  Users,
} from 'lucide-react';

/* ─── Constants ────────────────────────────────────────────────────── */

// Mapping from advisory team member role enum -> short label and icon
// (matches PartnerCategorizable / DealTeamMember role enum values)
const ROLE_META = {
  cbv: { short: 'CBV', icon: Scale },
  business_broker: { short: 'BROKER', icon: Briefcase },
  lawyer: { short: 'LAWYER', icon: Gavel },
  accountant: { short: 'ACCOUNTANT', icon: Calculator },
  lender: { short: 'LENDER', icon: Landmark },
  cepa_coach: { short: 'CEPA', icon: Compass },
};

const ALL_ROLES = ['cbv', 'business_broker', 'lawyer', 'accountant', 'lender', 'cepa_coach'];

// Purpose presets for the workspace picker. Each option drives the
// default name template + the suggested-advisor pre-selection. The
// purpose itself isn't a backend field — we encode it via the name
// the user picks (or edits) and the advisors selected. Keeps the
// DealTeam schema unchanged.
const PURPOSE_OPTIONS = [
  {
    id: 'sale',
    label: 'Active sale',
    blurb: 'Coordinate the sale of your business with your full team.',
    suggestedRoles: ['business_broker', 'lawyer', 'accountant', 'cbv', 'lender', 'cepa_coach'],
    nameTemplate: (companyName) => companyName ? `Sale of ${companyName}` : 'My business sale',
  },
  {
    id: 'financing',
    label: 'Financing exploration',
    blurb: 'Explore deal-financing options with a lender — no full sale required yet.',
    suggestedRoles: ['lender', 'accountant'],
    nameTemplate: () => 'Financing options',
  },
  {
    id: 'succession',
    label: 'Succession planning',
    blurb: 'Long-term planning with your accountant, lawyer, and exit planner.',
    suggestedRoles: ['cepa_coach', 'accountant', 'lawyer'],
    nameTemplate: () => 'Succession planning',
  },
  {
    id: 'other',
    label: 'Something else',
    blurb: 'Custom workspace for any other coordinated effort.',
    suggestedRoles: [],
    nameTemplate: () => '',
  },
];

const STATUS_DOT_COLOR = {
  active: 'bg-green-500',
  invited: 'bg-[hsl(var(--succeedo-orange))]',
  added: 'bg-blue-500',
  off_platform: 'bg-[hsl(var(--muted-foreground))]/40',
  exploring_options: 'bg-amber-500',
};

/* ─── Helpers ──────────────────────────────────────────────────────── */

function deriveInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/* ─── StartDealTeamModal ──────────────────────────────────────────── */

const StartDealTeamModal = ({
  isOpen,
  onClose,
  ownerCompanyName,
  advisoryTeamMembers = [],
  onCreate,
  onFindPartner,
  onGoToTeamPage,
}) => {
  // Purpose picker drives the default name template and the
  // suggested-advisor pre-selection. Defaults to 'sale' for
  // backwards-compatible behaviour with the previous flow.
  const [purpose, setPurpose] = useState('sale');

  const purposeMeta = useMemo(
    () => PURPOSE_OPTIONS.find((p) => p.id === purpose) || PURPOSE_OPTIONS[0],
    [purpose],
  );

  // Default name derived from purpose + company name. User can edit.
  const defaultName = useMemo(
    () => purposeMeta.nameTemplate(ownerCompanyName),
    [purposeMeta, ownerCompanyName],
  );

  // Default member selection: members whose role appears in the
  // purpose's suggestedRoles, or all members for the 'sale' / 'other'
  // path. Empty suggestedRoles means "select all available" — that's
  // the conservative default when we don't have a strong opinion.
  const defaultSelectedIds = useMemo(() => {
    if (!purposeMeta.suggestedRoles.length) {
      return advisoryTeamMembers.map((m) => m.id);
    }
    return advisoryTeamMembers
      .filter((m) => purposeMeta.suggestedRoles.includes(m.role))
      .map((m) => m.id);
  }, [advisoryTeamMembers, purposeMeta]);

  const [workspaceName, setWorkspaceName] = useState(defaultName);
  const [selectedMemberIds, setSelectedMemberIds] = useState(defaultSelectedIds);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset everything when the modal re-opens — picks up roster changes
  // and a fresh purpose selection.
  useEffect(() => {
    if (isOpen) {
      setPurpose('sale');
      setWorkspaceName(defaultName);
      setSelectedMemberIds(defaultSelectedIds);
      setSubmitting(false);
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // When the user changes purpose, refresh the name + selection unless
  // they've already typed something custom in the name field.
  useEffect(() => {
    setWorkspaceName(defaultName);
    setSelectedMemberIds(defaultSelectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purpose]);

  const hasAdvisoryMembers = advisoryTeamMembers.length > 0;

  const filledRoleSet = useMemo(
    () => new Set(advisoryTeamMembers.map((m) => m.role)),
    [advisoryTeamMembers],
  );

  const unfilledRoles = useMemo(
    () => ALL_ROLES.filter((r) => !filledRoleSet.has(r)),
    [filledRoleSet],
  );

  const toggleMember = (memberId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  };

  const handleCreate = async () => {
    if (submitting || selectedMemberIds.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onCreate({
        name: workspaceName.trim() || defaultName || 'My workspace',
        advisoryTeamMemberIds: selectedMemberIds,
        additionalEmailInvites: [],
        purpose, // ahoy / agent context — backend ignores for now
      });
      // On success the container navigates away via window.location.href, so
      // no need to close. But fail-safe: close in case the caller doesn't.
      onClose && onClose();
    } catch (err) {
      console.error('Failed to create workspace:', err);
      setSubmitError(err?.message || 'Could not start your workspace. Please try again.');
      setSubmitting(false);
    }
  };

  const handleFindPartner = (role) => {
    onClose && onClose();
    if (onFindPartner) onFindPartner(role);
  };

  const handleGoToTeamPage = () => {
    onClose && onClose();
    if (onGoToTeamPage) onGoToTeamPage();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[hsl(var(--background))]/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] p-6 z-10">
          <div className="flex items-start justify-between mb-2">
            <h2 className="font-serif text-[22px] font-light text-[hsl(var(--foreground))]">
              Start a shared workspace
            </h2>
            <button
              onClick={onClose}
              className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
            Bring your team into a focused space — share documents, message together, and track
            milestones.
          </p>
          <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-2">
            One active workspace at a time. You can archive it later and start a new one when your
            focus shifts.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {hasAdvisoryMembers ? (
            <>
              {/* Purpose picker — drives default name + advisor pre-selection */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] mb-2 tracking-wide">
                  What&apos;s this workspace for?
                </label>
                <div className="space-y-2">
                  {PURPOSE_OPTIONS.map((option) => {
                    const selected = purpose === option.id;
                    return (
                      <label
                        key={option.id}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          selected
                            ? 'border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/5'
                            : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="workspace-purpose"
                          value={option.id}
                          checked={selected}
                          onChange={() => setPurpose(option.id)}
                          className="w-4 h-4 mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-[13px] font-medium text-[hsl(var(--foreground))]">
                            {option.label}
                          </div>
                          <div className="text-[12px] text-[hsl(var(--muted-foreground))]">
                            {option.blurb}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Workspace Name Input — prefilled from purpose, editable */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] mb-2 tracking-wide">
                  Workspace name
                </label>
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  className="w-full bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-lg px-4 py-3 text-[hsl(var(--foreground))] text-[14px] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none transition-colors"
                  placeholder="My workspace"
                />
              </div>

              {/* Roster Section */}
              <div>
                <div className="mb-4">
                  <h3 className="font-mono text-[11px] uppercase text-[hsl(var(--foreground))] mb-1 tracking-wide">
                    Pulled from your advisory team
                  </h3>
                  <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
                    {purposeMeta.suggestedRoles.length > 0
                      ? `We pre-selected the advisors most relevant for ${purposeMeta.label.toLowerCase()}. Adjust as you like.`
                      : "Uncheck anyone you don’t want in this workspace."}
                  </p>
                </div>

                <div className="space-y-2">
                  {advisoryTeamMembers.map((member) => {
                    const isSelected = selectedMemberIds.includes(member.id);
                    const meta = ROLE_META[member.role] || { short: (member.role || '').toUpperCase() };
                    const initials = deriveInitials(member.name);
                    const dotColor = STATUS_DOT_COLOR[member.status] || 'bg-[hsl(var(--muted-foreground))]/40';
                    const isOffPlatform = member.status === 'off_platform';

                    return (
                      <label
                        key={member.id}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-[hsl(var(--muted))]/30 transition-colors cursor-pointer"
                      >
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleMember(member.id)}
                          className="w-4 h-4 rounded border-[hsl(var(--border))] text-[hsl(var(--succeedo-orange))] focus:ring-[hsl(var(--succeedo-orange))] focus:ring-offset-0"
                        />

                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
                          <span className="text-[hsl(var(--foreground))] font-semibold text-[12px]">
                            {initials}
                          </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[13px] text-[hsl(var(--foreground))] font-medium">
                              {member.name || 'Unnamed'}
                            </span>
                            <span className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-2 py-0.5 rounded-full">
                              {meta.short}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] text-[hsl(var(--muted-foreground))]">
                              {member.firm}
                              {isOffPlatform && <span className="ml-1">(off-platform)</span>}
                            </span>
                            <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Selection Count */}
                <div className="mt-3">
                  {selectedMemberIds.length === 0 ? (
                    <p className="text-[12px] text-[hsl(var(--destructive))]">
                      Select at least one team member to start
                    </p>
                  ) : (
                    <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
                      {selectedMemberIds.length} of {advisoryTeamMembers.length} selected
                    </p>
                  )}
                </div>
              </div>

              {/* Gaps Section */}
              {unfilledRoles.length > 0 && (
                <div className="border-t border-[hsl(var(--border))] pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="font-mono text-[11px] uppercase text-[hsl(var(--foreground))] tracking-wide">
                      Roles you don&apos;t have yet
                    </h3>
                    <AlertCircle className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
                  </div>

                  <div className="space-y-2 mb-3">
                    {unfilledRoles.map((roleId) => {
                      const meta = ROLE_META[roleId] || { short: roleId.toUpperCase(), icon: Users };
                      const Icon = meta.icon;
                      return (
                        <div
                          key={roleId}
                          className="flex items-center justify-between p-2 rounded-lg hover:bg-[hsl(var(--muted))]/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full border-2 border-dashed border-[hsl(var(--border))] flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                            </div>
                            <span className="text-[13px] text-[hsl(var(--muted-foreground))] font-medium">
                              {meta.short}
                            </span>
                          </div>
                          <button
                            onClick={() => handleFindPartner(roleId)}
                            className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline"
                          >
                            + Find someone
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    You can add these later from your team workspace.
                  </p>
                </div>
              )}

              {submitError && (
                <p className="text-[12px] text-[hsl(var(--destructive))]">{submitError}</p>
              )}
            </>
          ) : (
            /* Empty State */
            <div className="py-12 text-center">
              <Users className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
              <h3 className="font-serif text-[20px] font-light text-[hsl(var(--foreground))] mb-2">
                No advisors yet
              </h3>
              <p className="text-[13px] text-[hsl(var(--muted-foreground))] mb-6 max-w-sm mx-auto">
                Add at least one advisor on your Team page first — that's who you'll share the
                workspace with.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={onClose}
                  className="border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-5 py-2 text-[14px] hover:bg-[hsl(var(--accent))] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGoToTeamPage}
                  className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] font-medium hover:opacity-90 transition-opacity"
                >
                  Go to Team Page
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasAdvisoryMembers && (
          <div className="sticky bottom-0 bg-[hsl(var(--card))] border-t border-[hsl(var(--border))] p-6 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-5 py-2 text-[14px] hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={submitting || selectedMemberIds.length === 0}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? 'Starting...' : 'Start workspace'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartDealTeamModal;
